import React, { useEffect, useRef, useState } from "react";
import SectionReveal from "./SectionReveal";

// Mobile browsers only allow a limited number of autoplaying videos at once
// (often just one), so mounting every background embed at page load leaves
// later ones blank. Instead, each embed mounts only while near the viewport —
// it claims the autoplay slot when visible and releases it when scrolled away.
// A poster image sits underneath as a fallback for when autoplay is blocked
// entirely (e.g. iOS Low Power Mode).
const useNearViewport = (ref) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "200px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
};

// Featured work — easy to add/replace entries.
const FEATURED_WORK = [
  {
    id: "thales-kickoff",
    eyebrow: "Featured Work · 01",
    title: "2025 Kickoff Video",
    subtitle: "Corporate / Brand Film",
    video: {
      vimeoId: "1205857895",
      vimeoHash: "58d5a7fb7d",
      // Native aspect of the source video (4224 x 1344) — cinemascope.
      aspect: "4224 / 1344",
      poster: `${process.env.PUBLIC_URL}/weaver-kickoff-poster.jpg`,
    },
    credit: {
      label: "Produced for",
      logo: {
        src: `${process.env.PUBLIC_URL}/logos/thales.png`,
        alt: "Thales",
        href: "https://www.thalesgroup.com/",
        className: "h-16 sm:h-20",
      },
      body: (
        <>
          Produced for <span className="text-white font-medium">Thales</span> —
          a global leader in defense, aerospace, and cybersecurity, with{" "}
          <span className="text-white font-medium">83,000+ employees</span>{" "}
          across <span className="text-white font-medium">68 countries</span>.
          This 2025 kickoff film was made to do one thing: get a room on its
          feet and pointed at the year ahead. Motion, music, and message,
          engineered to move people.
        </>
      ),
    },
  },
  {
    id: "imperva-ddos",
    eyebrow: "Featured Work · 02",
    title: "DDoS Protection Explainer",
    subtitle: "Animated / Brand Explainer",
    video: {
      vimeoId: "1205858112",
      vimeoHash: "1dc477d1f9",
      // Native 1920x1080.
      aspect: "16 / 9",
      poster: `${process.env.PUBLIC_URL}/imperva-ddos-poster.jpg`,
    },
    credit: {
      label: "Produced for",
      logo: {
        src: `${process.env.PUBLIC_URL}/logos/imperva.png`,
        alt: "Imperva, a Thales company",
        href: "https://www.imperva.com/",
        className: "h-20 sm:h-24",
      },
      body: (
        <>
          Produced for{" "}
          <span className="text-white font-medium">Imperva</span> — a global
          leader in application, API, and data security, defending the world's
          largest organizations against attacks that can take a business
          offline in seconds. This animated explainer turns an invisible,
          abstract threat into something you can see and instantly understand.
          Motion, voice, and pacing built to make complex security simple.
        </>
      ),
    },
  },
];

const FeaturedItem = ({ item, isFirst }) => {
  const { eyebrow, title, subtitle, video, credit } = item;
  const mediaRef = useRef(null);
  const inView = useNearViewport(mediaRef);
  return (
    <div
      className={isFirst ? "" : "mt-24 sm:mt-36 pt-16 sm:pt-24 border-t border-white/10"}
      data-testid={`featured-item-${item.id}`}
    >
      <SectionReveal>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 sm:mb-14">
          <div>
            <span className="block text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-4">
              {eyebrow}
            </span>
            <h2
              className="text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white"
              data-testid={`featured-video-headline-${item.id}`}
            >
              {title}
            </h2>
          </div>
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">
            {subtitle}
          </span>
        </div>
      </SectionReveal>

      <SectionReveal delay={0.1}>
        <div
          ref={mediaRef}
          className="relative w-full bg-[#0A0A0A] border border-white/10 overflow-hidden"
          style={{ aspectRatio: video.aspect }}
          data-testid={`featured-video-player-${item.id}`}
        >
          {video.poster && (
            <img
              src={video.poster}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          )}
          {inView && (
            <iframe
              src={`https://player.vimeo.com/video/${video.vimeoId}?h=${video.vimeoHash}&background=1&autoplay=1&loop=1&muted=1`}
              title={title}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              className="absolute inset-0 w-full h-full block scale-[1.03] sm:scale-[1.01]"
              data-testid={`featured-video-element-${item.id}`}
            />
          )}
        </div>
      </SectionReveal>

      <SectionReveal delay={0.25}>
        <div
          className="mt-12 sm:mt-16 grid md:grid-cols-12 gap-8 md:gap-12 items-start"
          data-testid={`featured-video-credit-${item.id}`}
        >
          <div className="md:col-span-4 lg:col-span-3">
            <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-5">
              {credit.label}
            </span>
            <a
              href={credit.logo.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={credit.logo.alt}
              className="inline-block focus:outline-none"
              data-testid={`featured-video-logo-link-${item.id}`}
            >
              <img
                src={credit.logo.src}
                alt={credit.logo.alt}
                loading="lazy"
                className={`${credit.logo.className} w-auto object-contain opacity-90 hover:opacity-100 transition-opacity duration-300`}
                data-testid={`featured-video-logo-${item.id}`}
              />
            </a>
          </div>
          <div className="md:col-span-8 lg:col-span-9">
            <p
              className="text-base sm:text-lg font-light text-zinc-300 leading-relaxed tracking-wide max-w-3xl"
              data-testid={`featured-video-blurb-${item.id}`}
            >
              {credit.body}
            </p>
          </div>
        </div>
      </SectionReveal>
    </div>
  );
};

const FeaturedVideo = () => {
  return (
    <section
      id="featured-video"
      data-testid="featured-video-section"
      className="relative bg-[#050505] py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        {FEATURED_WORK.map((item, idx) => (
          <FeaturedItem key={item.id} item={item} isFirst={idx === 0} />
        ))}
      </div>
    </section>
  );
};

export default FeaturedVideo;
